import {
  API_TAX_FILE,
  API_TAX_FILE_CONFIRM_UPLOAD,
  API_TAX_FILE_DOWNLOAD,
  API_TAX_FILE_HISTORY,
  API_TAX_FILE_PAYLOAD,
  API_TAX_FILE_PREVIEW,
  API_TAX_FILER,
  API_TAX_FILER_DEACTIVATE,
  API_TAX_FILER_OWNERS,
  API_TAX_FILERS,
  API_TAX_META,
  API_TAX_PRINT,
  API_TAX_RETURN,
  API_TAX_RETURN_CORRECT,
  API_TAX_RETURNS,
  API_TAX_RETURNS_APPROVE,
  API_TAX_RETURNS_REVIEW,
  API_TAX_RETURNS_SCAN,
  API_TAX_RETURNS_SUMMARY,
  API_TAX_RETURNS_UNAPPROVE,
  API_TAX_RETURNS_VOID,
  API_TAX_STATEMENT_CONSENT,
  API_TAX_STATEMENT_PDF,
  API_TAX_STATEMENT_TAKE_DOWN,
  API_TAX_STATEMENTS,
  API_TAX_STATEMENTS_PUBLISH,
} from 'config'

import Api from '../api'
import { downloadFile } from './documents'

/** Form types with their boxes and the threshold for the year asked about. */
export const getTaxMeta = (taxYear: number) => {
  return Api.get(API_TAX_META, { taxYear })
}

// ── the filer registry

export const getTaxFilers = (includeInactive = false) => {
  return Api.get(API_TAX_FILERS, { includeInactive })
}

export const getTaxFilerOwners = () => {
  return Api.get(API_TAX_FILER_OWNERS)
}

export const saveTaxFiler = (data: Record<string, any>) => {
  return Api.post(API_TAX_FILERS, data)
}

export const deactivateTaxFiler = (id: number) => {
  return Api.post(API_TAX_FILER_DEACTIVATE, {}, { id })
}

export const getTaxFiler = (id: number) => {
  return Api.get(API_TAX_FILER, {}, { id })
}

// ── the review area

/** Extract a form type for a year into reviewable returns. Safe to re-run. */
export const scanTaxReturns = (formType: string, taxYear: number) => {
  return Api.post(API_TAX_RETURNS_SCAN, { formType, taxYear })
}

export const getTaxReturnSummary = (taxYear: number, formType?: string) => {
  return Api.get(API_TAX_RETURNS_SUMMARY, { taxYear, ...(formType ? { formType } : {}) })
}

export const getTaxReturns = (query: Record<string, any>) => {
  return Api.get(API_TAX_RETURNS, query)
}

export const getTaxReturn = (id: number) => {
  return Api.get(API_TAX_RETURN, {}, { id })
}

export const updateTaxReturn = (id: number, data: Record<string, any>) => {
  return Api.put(API_TAX_RETURN, data, { id })
}

export const markTaxReturnsReviewed = (ids: number[]) => {
  return Api.post(API_TAX_RETURNS_REVIEW, { ids })
}

/** The gate. Nothing is filed or printed for a recipient until it has passed through here. */
export const approveTaxReturns = (ids: number[], overrideReason?: string) => {
  return Api.post(API_TAX_RETURNS_APPROVE, { ids, ...(overrideReason ? { overrideReason } : {}) })
}

export const unapproveTaxReturns = (ids: number[]) => {
  return Api.post(API_TAX_RETURNS_UNAPPROVE, { ids })
}

export const voidTaxReturns = (ids: number[], reason: string) => {
  return Api.post(API_TAX_RETURNS_VOID, { ids, reason })
}

export const correctTaxReturn = (id: number, correctionType: string) => {
  return Api.post(API_TAX_RETURN_CORRECT, { correctionType }, { id })
}

// ── filing

/** What would be sent, and whether it can be, without sending anything. */
export const previewTaxFiling = (data: Record<string, any>) => {
  return Api.post(API_TAX_FILE_PREVIEW, data)
}

export const fileTaxReturns = (data: Record<string, any>) => {
  return Api.post(API_TAX_FILE, data)
}

/** Record that somebody uploaded an IRIS export to the portal themselves. */
export const confirmTaxUpload = (id: number) => {
  return Api.post(API_TAX_FILE_CONFIRM_UPLOAD, {}, { id })
}

export const getTaxFilingHistory = (taxYear: number, formType?: string) => {
  return Api.get(API_TAX_FILE_HISTORY, { taxYear, ...(formType ? { formType } : {}) })
}

export const getTaxPayloadUrl = (id: number) => {
  return Api.get(API_TAX_FILE_PAYLOAD, {}, { id })
}

/** Every selected recipient copy in one PDF, downloaded as one file. */
export const printTaxReturns = async (data: Record<string, any>, fileName: string) => {
  const res: Blob = await Api.post(API_TAX_PRINT, data, {}, { responseType: 'blob' })
  downloadFile(fileName, res)
}

/**
 * One IRIS file, rebuilt from the returns and downloaded directly.
 *
 * Direct rather than through the archived copy: on the export channel somebody has to have this
 * file in hand to file at all, so it does not wait on object storage being reachable. `index`
 * picks the file when a year is split across several, which it is every 100 returns.
 */
export const downloadTaxFile = async (submissionId: number, index: number, fileName: string) => {
  const res: Blob = await Api.get(API_TAX_FILE_DOWNLOAD, { index }, { id: submissionId }, { responseType: 'blob' })
  downloadFile(fileName, res)
}

// ── furnishing the recipient copies online

/** What has been furnished for a year, and who is still owed paper. */
export const getTaxStatementStatus = (taxYear: number, formType?: string, filerId?: number) => {
  return Api.get(API_TAX_STATEMENTS, {
    taxYear,
    ...(formType ? { formType } : {}),
    ...(filerId ? { filerId } : {}),
  })
}

/**
 * Post the selected recipient copies where those recipients can reach them.
 *
 * The answer worth reading is `paperList`: posting cannot discharge the obligation for a recipient
 * who never agreed to electronic delivery, and a vendor has no portal at all.
 */
export const publishTaxStatements = (data: Record<string, any>) => {
  return Api.post(API_TAX_STATEMENTS_PUBLISH, data)
}

/** One recipient's statement, opened in a new tab rather than downloaded. */
export const taxStatementPdfUrl = (recordId: number) => {
  return API_TAX_STATEMENT_PDF.replace(':recordId', String(recordId))
}

export const takeDownTaxStatement = (deliveryId: number, reason: string) => {
  return Api.post(API_TAX_STATEMENT_TAKE_DOWN, { reason }, { deliveryId })
}

/** Record an agreement to electronic delivery taken on paper or over the phone, or a withdrawal. */
export const setTaxStatementConsent = (data: {
  recipientType: string
  recipientKey: string
  consented: boolean
  email?: string
  notes?: string
}) => {
  return Api.post(API_TAX_STATEMENT_CONSENT, data)
}
